import { JPBPriceIndexSource, JPB_MARK_SHARE_COLUMNS, JPB_MARK_SHARE_COLUMNS_INTRODUCED } from "./JPBState";

/** When `"false"`, the next JPB open starts from defaults instead of persisted sidebar choices. */
export const JPB_PERSIST_USER_SETTINGS_KEY = "jpb.persistUserSettings";
export const JPB_PERSISTED_YEAR_GROUPING_KEY = "jpb.appliedYearGrouping";
/** Legacy key — no longer persisted; cleared on startup and when clearing saved choices. */
export const JPB_LEGACY_PERSISTED_CALC_DATE_TO_KEY = "jpb.appliedCalculationDateTo";
export const JPB_PERSISTED_PRICE_RANGE_KEY = "jpb.priceRange";
export const JPB_PERSISTED_VISIBLE_COLUMNS_KEY = "jpb.visibleColumns";

export const JPB_REQUIRED_VISIBLE_COLUMN = "select-row";

export const JPB_PERSISTED_USER_SETTING_KEYS = [
   "jpb.areaUnit",
   "jpb.priceMode",
   "jpb.averageCalculationMethod",
   "jpb.priceIndexSource",
   "jpb.viewMode",
   JPB_PERSISTED_YEAR_GROUPING_KEY,
   JPB_PERSISTED_PRICE_RANGE_KEY,
   JPB_PERSISTED_VISIBLE_COLUMNS_KEY,
] as const;

export type PersistedJPBPriceRange = {
   min: number;
   max: number;
};

export function clearPersistedJPBUserSettings(): void {
   JPB_PERSISTED_USER_SETTING_KEYS.forEach((key) => localStorage.removeItem(key));
   localStorage.removeItem(JPB_LEGACY_PERSISTED_CALC_DATE_TO_KEY);
   localStorage.removeItem("jpb.persistedAssumptions");
   sessionStorage.removeItem("jpb.session.hydratedAssumptions");
}

/** Missing or unknown values return null so the caller can fall back to the product default. `"none"` is a real saved choice. */
export function parsePersistedPriceIndexSource(
   raw: string | null
): JPBPriceIndexSource | null {
   if (raw == null || raw === "") return null;
   switch (raw) {
      case JPBPriceIndexSource.None:
      case JPBPriceIndexSource.Molbak:
      case JPBPriceIndexSource.Molbak20ha:
      case JPBPriceIndexSource.AgroCura:
         return raw;
      default:
         return null;
   }
}

export function parsePersistedPriceRange(
   raw: string | null
): PersistedJPBPriceRange | null {
   if (raw == null || raw === "") return null;
   try {
      const parsed = JSON.parse(raw) as { min?: unknown; max?: unknown };
      const min = typeof parsed.min === "number" ? parsed.min : Number(parsed.min);
      const max = typeof parsed.max === "number" ? parsed.max : Number(parsed.max);
      if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
      if (min < 0 || max < min) return null;
      return { min, max };
   } catch {
      return null;
   }
}

export function serializePersistedPriceRange(min: number, max: number): string {
   return JSON.stringify({ min, max });
}

export function parsePersistedVisibleColumns(raw: string | null): string[] | null {
   if (raw == null || raw === "") return null;
   try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return null;
      const columns: string[] = [];
      const seen = new Set<string>();
      for (const item of parsed) {
         if (typeof item !== "string" || item === "") continue;
         if (seen.has(item)) continue;
         seen.add(item);
         columns.push(item);
      }
      if (columns.length === 0) return null;
      if (!seen.has(JPB_REQUIRED_VISIBLE_COLUMN)) {
         columns.unshift(JPB_REQUIRED_VISIBLE_COLUMN);
      }
      return appendMarkShareColumnsIfMissing(columns);
   } catch {
      return null;
   }
}

export function serializePersistedVisibleColumns(columns: readonly string[]): string {
   return JSON.stringify(columns);
}

/** Adds omdrift / §3 columns to an older saved list. Already-present keys are left in place. */
export function appendMarkShareColumnsIfMissing(columns: readonly string[]): string[] {
   if (columns.includes(JPB_MARK_SHARE_COLUMNS_INTRODUCED)) {
      return [...columns];
   }
   const next = [...columns];
   for (const column of JPB_MARK_SHARE_COLUMNS) {
      if (!next.includes(column.columnKey)) next.push(column.columnKey);
   }
   next.push(JPB_MARK_SHARE_COLUMNS_INTRODUCED);
   return next;
}
